/*
Copyright 2025 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { logger } from "matrix-js-sdk/lib/logger";
import { type MatrixClient, type Room } from "matrix-js-sdk";
import { type MatrixRTCSession } from "matrix-js-sdk/lib/matrixrtc";

import { callTraceJournal } from "../telemetry";
import {
  createRoomMembersHydrator,
  type RoomMembersHydrationSource,
  type RoomMembersHydrator,
} from "./RoomMembersHydrator";
import {
  createRtcMembersHydrator,
  type RtcMembersHydrator,
} from "./RtcMembersHydrator";
import { parseParticipantIdentity } from "./participantIdentity";

export type MembershipRecoveryReason =
  | "room_member_missing"
  | "rtc_member_missing"
  | "participant_unmatched";
type RtcMembershipRecoveryReason = Exclude<
  MembershipRecoveryReason,
  "room_member_missing"
>;
type CoordinatorRtcSkipReason =
  | "foreign_room"
  | "invalid_participant_identity"
  | "missing_user_id"
  | "missing_device_id"
  | "already_present"
  | "room_member_still_missing";
type RtcRecoveryContext = Omit<
  RecoverMissingMembershipsContext,
  "reason"
> & {
  reason: RtcMembershipRecoveryReason;
};

export interface RecoverMissingMembershipsContext {
  userId?: string;
  deviceId?: string;
  participantIdentity?: string;
  source: RoomMembersHydrationSource;
  reason: MembershipRecoveryReason;
}

export interface MembershipRecoveryCoordinator {
  recoverMissingMemberships(
    roomId: string,
    context: RecoverMissingMembershipsContext,
  ): Promise<boolean>;
}

interface MembershipRecoveryCoordinatorDeps {
  roomMembersHydrator?: RoomMembersHydrator;
  rtcMembersHydrator?: RtcMembersHydrator;
}

class DefaultMembershipRecoveryCoordinator
  implements MembershipRecoveryCoordinator
{
  private readonly roomMembersHydrator: RoomMembersHydrator;
  private readonly rtcMembersHydrator: RtcMembersHydrator;

  public constructor(
    private readonly client: MatrixClient,
    private readonly room: Room,
    private readonly rtcSession: MatrixRTCSession,
    deps: MembershipRecoveryCoordinatorDeps = {},
  ) {
    this.roomMembersHydrator =
      deps.roomMembersHydrator ??
      createRoomMembersHydrator(this.client, this.room);
    this.rtcMembersHydrator =
      deps.rtcMembersHydrator ?? createRtcMembersHydrator(this.client, this.room);
  }

  public async recoverMissingMemberships(
    roomId: string,
    context: RecoverMissingMembershipsContext,
  ): Promise<boolean> {
    if (roomId !== this.room.roomId) {
      const rtcContext = this.toRtcRecoveryContext(context);
      if (rtcContext) {
        this.recordRtcSkipped(rtcContext, "foreign_room");
      }
      logger.warn(
        "[MembershipRecoveryCoordinator] Получен запрос для чужой комнаты",
        {
          expectedRoomId: this.room.roomId,
          roomId,
          reason: context.reason,
        },
      );
      return false;
    }

    const parsedIdentity = parseParticipantIdentity(context.participantIdentity);
    const userId = context.userId ?? parsedIdentity?.userId;
    const deviceId = context.deviceId ?? parsedIdentity?.deviceId;

    if (context.participantIdentity && !parsedIdentity) {
      const rtcContext = this.toRtcRecoveryContext(context);
      if (rtcContext) {
        this.recordRtcSkipped(rtcContext, "invalid_participant_identity");
      }
      logger.warn(
        "[MembershipRecoveryCoordinator] Пропускаем recovery, identity не удалось распарсить",
        {
          roomId,
          reason: context.reason,
          participantIdentity: context.participantIdentity,
        },
      );
      return false;
    }

    if (!userId) {
      const rtcContext = this.toRtcRecoveryContext(context);
      if (rtcContext) {
        this.recordRtcSkipped(rtcContext, "missing_user_id");
      }
      logger.warn(
        "[MembershipRecoveryCoordinator] Пропускаем recovery без userId",
        {
          roomId,
          reason: context.reason,
          participantIdentity: context.participantIdentity ?? null,
        },
      );
      return false;
    }

    const roomMember = this.room.getMember(userId);
    const rtcMemberVisible =
      !!deviceId && this.hasRtcMembership(userId, deviceId);

    if (context.reason === "room_member_missing") {
      return this.recoverRoomMember(roomId, userId, context.source);
    }
    const rtcContext = this.toRtcRecoveryContext(context);
    if (!rtcContext) return false;

    if (!deviceId) {
      this.recordRtcSkipped(
        { ...rtcContext, userId },
        "missing_device_id",
      );
      logger.warn(
        "[MembershipRecoveryCoordinator] Пропускаем RTC recovery без deviceId",
        {
          roomId,
          reason: context.reason,
          userId,
        },
      );
      return false;
    }

    if (roomMember && !rtcMemberVisible) {
      return this.recoverRtcMember(
        roomId,
        userId,
        deviceId,
        rtcContext.source,
        rtcContext.reason,
        rtcContext.participantIdentity,
      );
    }

    if (!roomMember) {
      // Сначала подтягиваем m.room.member: после этого MatrixRTC membership может
      // проявиться сам при пересчете сессии, если событие уже было локально.
      await this.recoverRoomMember(roomId, userId, context.source);
      this.rtcSession.onRoomMemberUpdate();

      if (this.hasRtcMembership(userId, deviceId)) {
        return true;
      }

      if (!this.room.getMember(userId)) {
        this.recordRtcSkipped(
          { ...rtcContext, userId, deviceId },
          "room_member_still_missing",
        );
        return false;
      }

      return this.recoverRtcMember(
        roomId,
        userId,
        deviceId,
        rtcContext.source,
        rtcContext.reason,
        rtcContext.participantIdentity,
      );
    }

    this.recordRtcSkipped(
      { ...rtcContext, userId, deviceId },
      "already_present",
    );
    return rtcMemberVisible;
  }

  private async recoverRoomMember(
    roomId: string,
    userId: string,
    source: RoomMembersHydrationSource,
  ): Promise<boolean> {
    const resolved = await this.roomMembersHydrator.ensureRoomMembersHydrated(
      roomId,
      {
        forceServer: true,
        reason: "room_member_missing",
        source,
        userId,
      },
    );
    this.rtcSession.onRoomMemberUpdate();
    return resolved;
  }

  private async recoverRtcMember(
    roomId: string,
    userId: string,
    deviceId: string,
    source: RoomMembersHydrationSource,
    reason: RtcMembershipRecoveryReason,
    participantIdentity: string | undefined,
  ): Promise<boolean> {
    const resolved = await this.rtcMembersHydrator.ensureRtcMembersHydrated(
      roomId,
      {
        forceServer: true,
        reason,
        source,
        userId,
        deviceId,
        participantIdentity,
      },
    );
    this.rtcSession.onRTCSessionMemberUpdate();
    return resolved || this.hasRtcMembership(userId, deviceId);
  }

  private recordRtcSkipped(
    context: RtcRecoveryContext,
    skipReason: CoordinatorRtcSkipReason,
  ): void {
    const parsedIdentity = parseParticipantIdentity(context.participantIdentity);
    callTraceJournal.record({
      eventType: "matrix.rtc_members_recovery_skipped",
      stage: "matrix",
      outcome: "cancelled",
      reasonCode: skipReason,
      payload: {
        mode: "coordinator",
        reason: context.reason,
        forceServer: true,
        source: context.source,
        userId: context.userId ?? parsedIdentity?.userId ?? null,
        deviceId: context.deviceId ?? parsedIdentity?.deviceId ?? null,
        participantIdentity: context.participantIdentity ?? null,
        skipReason,
      },
    });
  }

  private toRtcRecoveryContext(
    context: RecoverMissingMembershipsContext,
  ): RtcRecoveryContext | null {
    if (!this.isRtcRecoveryReason(context.reason)) return null;
    return { ...context, reason: context.reason };
  }

  private isRtcRecoveryReason(
    reason: MembershipRecoveryReason,
  ): reason is RtcMembershipRecoveryReason {
    return reason !== "room_member_missing";
  }

  private hasRtcMembership(userId: string, deviceId: string): boolean {
    return this.rtcSession.memberships.some(
      (membership) =>
        membership.sender === userId && membership.deviceId === deviceId,
    );
  }
}

export function createMembershipRecoveryCoordinator(
  client: MatrixClient,
  room: Room,
  rtcSession: MatrixRTCSession,
  deps: MembershipRecoveryCoordinatorDeps = {},
): MembershipRecoveryCoordinator {
  return new DefaultMembershipRecoveryCoordinator(
    client,
    room,
    rtcSession,
    deps,
  );
}
